/**
 * Marks every call to the FinalKey API as coming from the web app, and
 * lets the browser carry the API's cookies across origins.
 *
 * The backend keys the browser session model on `X-Client: web`
 * (backend/src/auth/web-session.ts): the 30-day device-trust token is
 * then set as an httpOnly cookie instead of being returned in the body,
 * so a script injected into the page cannot read it. In development the
 * API is same-origin (/__api) and cookies flow by default; in production
 * app.myfinalkey.com calls api.myfinalkey.com, which needs
 * `credentials: 'include'` on fetch and `withCredentials` on axios.
 *
 * Installed once from main.tsx before the app module is evaluated. Only
 * requests to the API origin are touched; a request to any other host
 * goes through untouched.
 */
import http from '../../../src/services/http';
import { API_URL } from '../../../src/config';

const CLIENT_HEADER = 'X-Client';
const CLIENT_VALUE = 'web';

function apiOrigin(): string {
  try {
    return new URL(API_URL, window.location.href).origin;
  } catch {
    return '';
  }
}

function isApiRequest(input: RequestInfo | URL, origin: string): boolean {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  try {
    const resolved = new URL(url, window.location.href);
    if (resolved.origin !== origin) return false;
    // In development the API lives under /__api on the dev server's
    // origin; nothing else on that origin is the API.
    if (API_URL.startsWith(window.location.origin)) {
      const prefix = new URL(API_URL).pathname.replace(/\/$/, '');
      return prefix === '' || resolved.pathname.startsWith(prefix);
    }
    return true;
  } catch {
    return false;
  }
}

export function installWebApiClient(): void {
  const origin = apiOrigin();
  if (!origin) return;

  // axios instance used by the services layer.
  http.defaults.withCredentials = true;
  http.defaults.headers.common[CLIENT_HEADER] = CLIENT_VALUE;

  // Raw fetch calls scattered through the auth screens and sessionManager.
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!isApiRequest(input, origin)) return nativeFetch(input, init);
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has(CLIENT_HEADER)) headers.set(CLIENT_HEADER, CLIENT_VALUE);
    return nativeFetch(input, { ...init, headers, credentials: 'include' });
  };
}
